import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  input,
  OnInit, output,
  signal,
  viewChild,
  viewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { ProductLabelTypeEnum } from '@api/models';
import { TranslatePipe } from '@ngx-translate/core';
import { RouterLinkWithLang } from '../../directives/router-link-with-lang/router-link-with-lang.directive';
import { CarouselItem } from '../../interfaces/carousel-item.interface';
import { DeviceService } from '../../services/device-detector/device.service';
import { PreventScrollService } from '../../services/prevent-scroll.service';
import { ScrollToService } from '../../services/scroll-to/scroll-to.service';

@Component({
  selector: 'carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgTemplateOutlet, NgClass, RouterLink, RouterLinkWithLang, TranslatePipe],
})
export class CarouselComponent implements OnInit, AfterViewInit {
  protected preventScrollService = inject(PreventScrollService);
  protected scrollToService = inject(ScrollToService);
  protected deviceService = inject(DeviceService);
  protected cdr = inject(ChangeDetectorRef);

  readonly carouselItems = input<CarouselItem[]>(undefined);
  readonly mediaAltText = input<string>('');
  readonly itemsPerSlide = input(1);
  readonly autoSwipe = input(false);
  readonly showThumbnails = input(true);
  readonly showPrice = input(false);
  readonly showDiscount = input(true);
  readonly showLabel = input(true);
  /**
   * A frame of this shape (a CSS `aspect-ratio`, e.g. `4 / 3`) for every slide, the image fitted
   * inside it, so the carousel takes its full height before any image has loaded.
   */
  readonly slideAspectRatio = input<string | null>(null);
  readonly mediaClicked = output<void>();

  readonly carouselListRef = viewChild<ElementRef>('carouselListRef');
  readonly carouselTrackRef = viewChild<ElementRef>('carouselTrackRef');
  readonly thumbnailItemRef = viewChildren<ElementRef>('thumbnailItemRef');


  activeSlideIndex = signal<number>(0);
  carouselItemWidth = signal<number>(0);

  protected isModal: boolean = false;

  private slideWidth: number = 0;
  private sliderTimeout: ReturnType<typeof setTimeout>;
  private slideChangeTime = 3000;

  private swipeCoord: [number, number];
  private swipeTime: number;

  ngOnInit(): void {
    this.setDiscountValue();
  }

  ngAfterViewInit() {
    this.getSlideWidth();
    this.setCarouselItemWidth();

    if (this.autoSwipe()) {
      this.showSlides();
    }
  }

  protected getSlideWidth(): void {
    if (this.deviceService.isPlatformServer()) { return; }
    this.slideWidth = this.carouselListRef().nativeElement.getBoundingClientRect().width;
  }

  protected showActiveSlide(withTransition = true): void {
    this.getSlideWidth();

    this.carouselTrackRef().nativeElement.style.transition = withTransition ? 'transform 0.5s' : 'none';
    this.carouselTrackRef().nativeElement.style.transform = `
    translate3d(-${(this.activeSlideIndex() * this.slideWidth) / this.itemsPerSlide()}px, 0px, 0px)
    `;
  }

  prev(): void {
    if (this.activeSlideIndex() > 0) {
      this.activeSlideIndex.update(value => value - 1);
    } else if (this.activeSlideIndex() === 0) {
      this.activeSlideIndex.set(this.carouselItems().length - 1);
    }
    this.showActiveSlide();
  }

  next(): void {
    if (this.activeSlideIndex() < this.carouselItems().length - 1) {
      this.activeSlideIndex.update(value => value + 1);
    } else {
      this.activeSlideIndex.set(0);
    }
    this.showActiveSlide();
  }

  swipe(e: TouchEvent, action: string): void {
    const coord: [number, number] = [e.changedTouches[0].clientX, e.changedTouches[0].clientY];
    const time = new Date().getTime();

    if (action === 'start') {
      this.swipeCoord = coord;
      this.swipeTime = time;
      clearTimeout(this.sliderTimeout);
    } else if (action === 'end') {
      if (!this.swipeCoord) {
        return;
      }

      const direction = [coord[0] - this.swipeCoord[0], coord[1] - this.swipeCoord[1]];
      const duration = time - this.swipeTime;

      if (duration < 1000
        && Math.abs(direction[0]) > 30
        && Math.abs(direction[0]) > Math.abs(direction[1] * 3)
      ) {
        const swipe = direction[0] < 0 ? 'next' : 'previous';

        if (swipe === 'next') {
          this.next();
          this.scrollToActiveThumbnail();
        } else {
          this.prev();
          this.scrollToActiveThumbnail();
        }
      }

      if (this.autoSwipe()) {
        this.showSlides();
      }
    }
  }

  getMediaUrl(carouselItem: CarouselItem): string {
    const carouselItemUrl = this.isModal
      ? carouselItem?.media?.variantsUrls?.large
      : carouselItem?.media?.variantsUrls?.medium;
    return carouselItemUrl;
  }

  getLabelClass(carouselItem: CarouselItem): string {
    switch (carouselItem?.label.type) {
      case ProductLabelTypeEnum.New:
        return 'carousel-item-label-new';
      case ProductLabelTypeEnum.Top:
        return 'carousel-item-label-top';
      case ProductLabelTypeEnum.Sale:
        return 'carousel-item-label-sale';
    }
  }

  onMediaClick(activeSlideIndex: number) {
    this.setActiveSlide(activeSlideIndex);
    this.mediaClicked.emit();
  }

  setActiveSlide(activeSlideIndex: number): void {
    this.activeSlideIndex.set(activeSlideIndex);
    this.scrollToActiveThumbnail();
    this.showActiveSlide(false);
  }

  private setDiscountValue(): void {
    const carouselItems = this.carouselItems();
    if (!carouselItems?.length || !this.showDiscount()) { return; }

    carouselItems.forEach((carouselItem: CarouselItem) => {
      carouselItem.discountValue = Math.ceil(
        (carouselItem.oldPrice - carouselItem.price) / carouselItem.oldPrice * 100
      );
    });
  }

  private setCarouselItemWidth(): void {
    this.carouselItemWidth.set((100 / this.itemsPerSlide()));
    this.cdr.detectChanges();
  }

  private showSlides(): void {
    const carouselItems = this.carouselItems();
    if (this.deviceService.isPlatformServer() || !carouselItems) { return; }

    if (this.activeSlideIndex() === carouselItems.length - 1) {
      this.activeSlideIndex.set(0);
    }

    this.showActiveSlide();

    this.sliderTimeout = setTimeout(() => {
      this.activeSlideIndex.update(value => value + 1);
      this.showSlides();
    }, this.slideChangeTime);
  }

  scrollToActiveThumbnail(): void {
    const activeThumbnailEl = this.thumbnailItemRef()
      .find((_element, index) => index === this.activeSlideIndex())?.nativeElement;

    if (activeThumbnailEl) {
      activeThumbnailEl.scrollIntoView({ block: 'end', inline: 'nearest', behavior: 'smooth' });
    }
  }
}
